import { useSelector } from 'react-redux';
import { MatchedGuestProfiles } from '@mews-commander/guest-profile';
import { hasReadOnlyPrivilegesForCustomerData } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { WithGuideLink } from '@mews-commander/types';
import { openInBrowserTab } from '@mews-commander/utils';
import { LocalizedText, lt } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Banner, Container, Link, Stack } from '@optimus-web/core';
// CORE DEPENDENCY
import { GuestDetailPanel, MatchesComparison, SubmitHandlerParams } from './components';
import { useGuestDetailDialog } from './hooks';

interface AccountMergeContentWrapperProps
    extends WithGuideLink,
        Pick<SubmitHandlerParams, 'navigateToNextMatch' | 'mergeMode'> {
    matchedGuestProfiles: MatchedGuestProfiles;
}

export const AccountMergeContentWrapper = ({
    matchedGuestProfiles,
    guideLink,
    mergeMode,
    navigateToNextMatch,
}: AccountMergeContentWrapperProps) => {
    const { profiles, mergeInfo } = matchedGuestProfiles;
    const readOnlyForm = useSelector(hasReadOnlyPrivilegesForCustomerData);
    const {
        guestDetailDialogProps,
        handleOpenGuestDetailDialog,
        selectedGuestId,
        selectedGuestFullName: fullName,
    } = useGuestDetailDialog(profiles);

    return (
        <Container width="80%" align="center" tabletMax={{ width: '90%' }}>
            <Stack vertical spacing="800">
                <Stack vertical spacing="200">
                    {readOnlyForm ? (
                        <Banner
                            centered
                            variant="warning"
                            primaryText={<LocalizedText textKey={TextKey.ReadOnlyPrivilege} />}
                        />
                    ) : null}
                    {!mergeInfo.isMergePossible ? (
                        <Banner
                            variant="warning"
                            primaryText={lt('AccountMergeMergeBlockedBannerTitle')}
                            secondaryText={lt('AccountMergeMergeBlockedBannerDescription')}
                            action={
                                <Link variant="basic" onClick={() => openInBrowserTab(guideLink)}>
                                    {lt('AccountMergeMergeBlockedBannerAction')}
                                </Link>
                            }
                        />
                    ) : null}
                </Stack>
                <MatchesComparison
                    matchedGuestProfiles={matchedGuestProfiles}
                    setSelectedGuestId={handleOpenGuestDetailDialog}
                    mergePossible={mergeInfo.isMergePossible}
                    mergeMode={mergeMode}
                    navigateToNextMatch={navigateToNextMatch}
                    readOnlyForm={readOnlyForm}
                />
                {selectedGuestId ? (
                    <GuestDetailPanel {...guestDetailDialogProps} guestId={selectedGuestId} fullName={fullName} />
                ) : null}
            </Stack>
        </Container>
    );
};
